import * as Api from '@ViewModels';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';
import { ApprovalHistoryResourceType, ApprovalHistoryResourceUrlSegments, IApprovalHistoryEntry } from './types';

export const APPROVAL_HISTORY_QUERY_KEY = 'approvalHistory';

/** Newest-first; render top-down without sorting. An empty array means a first-round piece, not a miss. */
export const useApprovalHistoryQuery = ({
	resourceId,
	resourceType,
	enabled = true,
	impersonationContext,
	liveRoundId,
}: {
	resourceId: string | null | undefined;
	resourceType: ApprovalHistoryResourceType | undefined;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	/**
	 * A supersede always raises a replacement, so a changed live round means the history grew. This key
	 * is the contract; `invalidateApprovalHistory` only covers the window before the host re-renders.
	 */
	liveRoundId?: string | null;
}) => {
	const userSession = useUserSession();
	return useQuery({
		enabled: enabled && !!resourceId && !!resourceType,
		// Keyed on the impersonated user too: the endpoint answers per user, so two users in one
		// account would otherwise share a cache entry across different authorization.
		queryKey: [
			APPROVAL_HISTORY_QUERY_KEY,
			resourceType,
			resourceId,
			impersonationContext?.account?.id,
			impersonationContext?.user?.id,
			liveRoundId,
		],
		// The key moves whenever a round is superseded; without this the panel blanks until it reloads.
		placeholderData: keepPreviousData,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<IApprovalHistoryEntry[] | null>({
				path: Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `${ApprovalHistoryResourceUrlSegments[resourceType!]}/${encodeURIComponent(
						resourceId!
					)}/approvalHistory`,
				}),
				method: 'GET',
				// A colleague's piece 404s rather than 403ing, as does a missing one, so null means
				// "nothing to show you" — distinct from an empty array, which means no earlier rounds.
				throwOn404: false,
			}),
		meta: {
			// A details page the advisor did not come to for the history; it says so where the rounds
			// would have been, so the toast is suppressed while the failure is still logged.
			toastErrorMessage: () => '',
			errorLogging: { category: 'Compliance', action: 'ApprovalHistoryQuery-Error' },
		} satisfies LevStandardQueryMeta,
	});
};

export const invalidateApprovalHistory = (resource?: {
	resourceId: string;
	resourceType: ApprovalHistoryResourceType;
}) => {
	return reactQueryClient.invalidateQueries({
		queryKey: resource
			? [APPROVAL_HISTORY_QUERY_KEY, resource.resourceType, resource.resourceId]
			: [APPROVAL_HISTORY_QUERY_KEY],
	});
};
